/**
 * AssetUploadTab
 *
 * Upload tab content: Image upload + Brand selection + Brand assets
 * Extracted from original AssetContextPanel
 *
 * @accessibility WCAG 2.1 AA compliant
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { Upload, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { BrandContextSection } from './BrandContextSection';
import { ReferenceImageUploadPanel } from './ReferenceImageUploadPanel';
import type { ReferenceSlot } from './GenerationFormV2';

interface AssetUploadTabProps {
  selectedFile: File | null;
  preview: string | null;
  onFileChange: (file: File | null) => void;
  selectedBrandId: string | null;
  onBrandChange: (brandId: string | null) => void;
  mask?: Blob | null;
  referenceSlots?: ReferenceSlot[];
  onReferenceSlotsChange?: (slots: ReferenceSlot[]) => void;
  maxReferenceImages?: number;
  onInsertMention?: (tag: string) => void;
}

export function AssetUploadTab({
  selectedFile: _selectedFile,
  preview,
  onFileChange,
  selectedBrandId,
  onBrandChange,
  mask,
  referenceSlots,
  onReferenceSlotsChange,
  maxReferenceImages = 0,
  onInsertMention,
}: AssetUploadTabProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  // Convert mask Blob to URL for preview
  const maskPreview = useMemo(() => {
    if (!mask) return null;
    return URL.createObjectURL(mask);
  }, [mask]);

  useEffect(() => {
    return () => { if (maskPreview) URL.revokeObjectURL(maskPreview); };
  }, [maskPreview]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileChange(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      onFileChange(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleClear = () => {
    onFileChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto p-2 space-y-4 bg-neutral-50 dark:bg-neutral-900/50">
      {/* Section Title */}
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
          Asset & Context
        </h3>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          {t('generation.uploadAndSelectBrand')}
        </p>
      </div>

      {/* Image Upload */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {t('generation.sourceImage')}
        </label>

        {preview ? (
          <div className="space-y-2">
            <div className="flex justify-center">
              <div className="relative rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                <img src={preview} alt="Preview" className="w-auto h-auto max-h-[200px] object-contain" />
                <button
                  type="button"
                  onClick={handleClear}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors shadow-lg"
                  aria-label={t('generation.removeImage')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Mask Preview */}
            {maskPreview && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
                    {t('generation.currentMask')}
                  </label>
                  <span className="text-2xs text-neutral-500 dark:text-neutral-500">
                    ⬜ Masked • ⬛ Original
                  </span>
                </div>
                <div className="flex justify-center">
                  <div className="rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                    <img
                      src={maskPreview}
                      alt="Mask Preview"
                      className="w-auto h-auto max-h-[120px] object-contain"
                      style={{ filter: 'invert(1)' }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={cn(
                'flex flex-col items-center justify-center p-8 rounded-lg border-2 border-dashed cursor-pointer transition-colors',
                isDragOver
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/20'
                  : 'border-neutral-300 dark:border-neutral-700 hover:border-orange-400'
              )}
            >
              <Upload className="h-8 w-8 text-neutral-400 mb-2" />
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
                {t('generation.clickOrDragImage')}
              </p>
              <p className="text-xs text-neutral-400">PNG, JPG, WebP (max 16 MB)</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
          </>
        )}
      </div>

      {/* Referenzbilder (nur wenn Modell multi-ref unterstützt) */}
      {maxReferenceImages > 1 && referenceSlots && onReferenceSlotsChange && (
        <ReferenceImageUploadPanel
          slots={referenceSlots}
          onSlotsChange={onReferenceSlotsChange}
          onInsertMention={onInsertMention ?? (() => {})}
        />
      )}

      {/* Brand Context Section */}
      <BrandContextSection
        selectedBrandId={selectedBrandId}
        onBrandChange={onBrandChange}
        showHeader={false}
      />
    </div>
  );
}
